/**
 * 🏷️ 智能标签管理器
 *
 * 功能特性：
 * - 跟随3D物体的标签文本
 * - 自动隐藏超出视野的标签
 * - 支持点击交互显示/隐藏
 * - 高性能批量更新
 * - 灵活的样式配置
 */

import * as THREE from 'three';

// 标签配置接口
export interface LabelConfig {
  /** 标签唯一标识 */
  id: string;
  /** 关联的3D对象 (可选, 优先级低于 updateCallback 和 targetPosition) */
  target?: THREE.Object3D;
  /** 目标世界坐标位置 (可选, 优先级低于 updateCallback) */
  targetPosition?: THREE.Vector3;
  /**
   * 位置更新回调 (最高优先级)
   * 返回 Vector3 更新位置, 返回 null 则隐藏标签
   */
  updateCallback?: () => THREE.Vector3 | null;
  /** 标签文本内容 */
  text: string;
  /** 标签类型样式 */
  type?: 'default' | 'vehicle' | 'device' | 'warning' | 'info';
  /** 是否启用自动跟随 */
  autoFollow?: boolean;
  /** 手动指定位置偏移 */
  offset?: { x: number; y: number; z: number };
  /** 是否可点击 */
  clickable?: boolean;
  /** 点击回调 */
  onClick?: (id: string) => void;
  /** 最大显示距离 */
  maxDistance?: number;
  /** 标签样式自定义 */
  style?: Partial<LabelStyle>;
}

// 标签样式配置
export interface LabelStyle {
  fontSize: string;
  color: string;
  backgroundColor: string;
  border: string;
  borderRadius: string;
  padding: string;
  fontFamily: string;
  boxShadow: string;
  backdropFilter: string;
  maxWidth: string;
  minWidth: string;
  zIndex: string;
}

// 标签实例接口
export interface LabelInstance {
  config: LabelConfig;
  element: HTMLElement;
  visible: boolean;
  lastPosition: THREE.Vector3;
  needsUpdate: boolean;
}

/**
 * 智能标签管理器
 */
export class LabelManager {
  private camera: THREE.Camera;
  private container: HTMLElement;
  private labels: Map<string, LabelInstance> = new Map();
  private raycaster: THREE.Raycaster = new THREE.Raycaster();
  private mouse: THREE.Vector2 = new THREE.Vector2();
  private isEnabled = true;
  private animationFrameId: number | null = null;

  // 默认样式配置
  private readonly DEFAULT_STYLES: Record<string, LabelStyle> = {
    default: {
      fontSize: '12px',
      color: '#ffffff',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '4px',
      padding: '4px 8px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
      backdropFilter: 'blur(4px)',
      maxWidth: '200px',
      minWidth: '60px',
      zIndex: '10000',
    },
    vehicle: {
      fontSize: '11px',
      color: '#ffffff',
      backgroundColor: 'rgba(0, 123, 255, 0.9)',
      border: '1px solid rgba(0, 123, 255, 0.5)',
      borderRadius: '6px',
      padding: '3px 6px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      boxShadow: '0 2px 12px rgba(0, 123, 255, 0.3)',
      backdropFilter: 'blur(8px)',
      maxWidth: '150px',
      minWidth: '50px',
      zIndex: '10001',
    },
    device: {
      fontSize: '10px',
      color: '#ffffff',
      backgroundColor: 'rgba(40, 167, 69, 0.9)',
      border: '1px solid rgba(40, 167, 69, 0.5)',
      borderRadius: '4px',
      padding: '2px 6px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      boxShadow: '0 2px 8px rgba(40, 167, 69, 0.3)',
      backdropFilter: 'blur(6px)',
      maxWidth: '120px',
      minWidth: '40px',
      zIndex: '10002',
    },
    warning: {
      fontSize: '12px',
      color: '#000000',
      backgroundColor: 'rgba(255, 193, 7, 0.95)',
      border: '1px solid rgba(255, 193, 7, 0.8)',
      borderRadius: '4px',
      padding: '4px 8px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      boxShadow: '0 2px 12px rgba(255, 193, 7, 0.4)',
      backdropFilter: 'blur(6px)',
      maxWidth: '180px',
      minWidth: '60px',
      zIndex: '10003',
    },
    info: {
      fontSize: '11px',
      color: '#ffffff',
      backgroundColor: 'rgba(23, 162, 184, 0.9)',
      border: '1px solid rgba(23, 162, 184, 0.5)',
      borderRadius: '4px',
      padding: '3px 6px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      boxShadow: '0 2px 8px rgba(23, 162, 184, 0.3)',
      backdropFilter: 'blur(6px)',
      maxWidth: '160px',
      minWidth: '50px',
      zIndex: '10004',
    },
  };

  constructor(camera: THREE.Camera, container: HTMLElement) {
    this.camera = camera;
    this.container = container;

    this.initializeEventListeners();
    this.createLabelContainer();
    this.startUpdateLoop();
  }

  /**
   * 初始化事件监听器
   */
  private initializeEventListeners(): void {
    // 窗口大小变化时重新计算位置
    window.addEventListener('resize', this.handleResize.bind(this));

    // 鼠标点击事件
    this.container.addEventListener('click', this.handleClick.bind(this));

    // 鼠标移动事件（用于射线检测）
    this.container.addEventListener(
      'mousemove',
      this.handleMouseMove.bind(this)
    );
  }

  /**
   * 处理窗口大小变化
   */
  private handleResize(): void {
    this.updateAllLabels();
  }

  /**
   * 处理鼠标点击
   */
  private handleClick(event: MouseEvent): void {
    this.updateMousePosition(event);

    // 检查是否点击了可交互的3D对象
    const clickableObjects: THREE.Object3D[] = [];
    this.labels.forEach(instance => {
      if (instance.config.clickable && instance.config.target) {
        clickableObjects.push(instance.config.target);
      }
    });

    if (clickableObjects.length > 0) {
      this.raycaster.setFromCamera(this.mouse, this.camera);
      const intersects = this.raycaster.intersectObjects(
        clickableObjects,
        true
      );

      if (intersects.length > 0) {
        // 找到被点击的对象对应的标签
        const clickedObject = intersects[0].object;
        this.labels.forEach(instance => {
          if (
            instance.config.clickable &&
            instance.config.target &&
            (instance.config.target === clickedObject ||
              instance.config.target.children.includes(clickedObject))
          ) {
            this.toggleLabel(instance.config.id);
            if (instance.config.onClick) {
              instance.config.onClick(instance.config.id);
            }
          }
        });
      }
    }
  }

  /**
   * 处理鼠标移动
   */
  private handleMouseMove(event: MouseEvent): void {
    this.updateMousePosition(event);
  }

  /**
   * 更新鼠标位置
   */
  private updateMousePosition(event: MouseEvent): void {
    const rect = this.container.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  }

  /**
   * 创建标签容器
   */
  private createLabelContainer(): void {
    let labelContainer = document.getElementById('v2x-label-container');
    if (!labelContainer) {
      labelContainer = document.createElement('div');
      labelContainer.id = 'v2x-label-container';
      labelContainer.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 9999;
      `;
      this.container.appendChild(labelContainer);
    }
  }

  /**
   * 启动更新循环
   */
  private startUpdateLoop(): void {
    const update = () => {
      if (this.isEnabled) {
        this.updateAllLabels();
      }
      this.animationFrameId = requestAnimationFrame(update);
    };
    this.animationFrameId = requestAnimationFrame(update);
  }

  /**
   * 停止更新循环
   */
  private stopUpdateLoop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * 添加标签
   */
  addLabel(config: LabelConfig): string {
    if (this.labels.has(config.id)) {
      console.warn(`标签 ${config.id} 已存在，将被替换`);
      this.removeLabel(config.id);
    }

    const element = this.createLabelElement(config);
    const instance: LabelInstance = {
      config,
      element,
      visible: config.autoFollow !== false,
      lastPosition: new THREE.Vector3(),
      needsUpdate: true,
    };

    this.labels.set(config.id, instance);

    if (instance.visible) {
      this.updateLabelPosition(instance);
    }

    return config.id;
  }

  /**
   * 移除标签
   */
  removeLabel(id: string): boolean {
    const instance = this.labels.get(id);
    if (!instance) return false;

    instance.element.remove();
    this.labels.delete(id);
    return true;
  }

  /**
   * 显示标签
   */
  showLabel(id: string): boolean {
    const instance = this.labels.get(id);
    if (!instance) return false;

    instance.visible = true;
    instance.needsUpdate = true;
    instance.element.style.display = 'block';
    instance.element.style.opacity = '0';

    // 动画显示
    requestAnimationFrame(() => {
      instance.element.style.transition =
        'opacity 0.3s ease, transform 0.3s ease';
      instance.element.style.opacity = '1';
      instance.element.style.transform += ' scale(1)';
    });

    return true;
  }

  /**
   * 隐藏标签
   */
  hideLabel(id: string): boolean {
    const instance = this.labels.get(id);
    if (!instance) return false;

    instance.visible = false;
    instance.element.style.transition =
      'opacity 0.3s ease, transform 0.3s ease';
    instance.element.style.opacity = '0';
    instance.element.style.transform += ' scale(0.95)';

    setTimeout(() => {
      if (!instance.visible) {
        instance.element.style.display = 'none';
      }
    }, 300);

    return true;
  }

  /**
   * 切换标签显示状态
   */
  toggleLabel(id: string): boolean {
    const instance = this.labels.get(id);
    if (!instance) return false;

    return instance.visible ? this.hideLabel(id) : this.showLabel(id);
  }

  /**
   * 更新标签文本
   */
  updateLabelText(id: string, text: string): boolean {
    const instance = this.labels.get(id);
    if (!instance) return false;

    instance.config.text = text;
    instance.element.textContent = text;
    return true;
  }

  /**
   * 更新标签的目标位置
   */
  updateLabelTargetPosition(id: string, position: THREE.Vector3): boolean {
    const instance = this.labels.get(id);
    if (!instance) return false;

    if (!instance.config.targetPosition) {
      instance.config.targetPosition = new THREE.Vector3();
    }

    instance.config.targetPosition.copy(position);
    instance.needsUpdate = true;
    return true;
  }

  /**
   * 创建标签元素
   */
  private createLabelElement(config: LabelConfig): HTMLElement {
    const label = document.createElement('div');
    label.className = `v2x-label label-${config.type || 'default'}`;

    // 获取样式配置
    const baseStyle = this.DEFAULT_STYLES[config.type || 'default'];
    const customStyle = config.style || {};
    const finalStyle = { ...baseStyle, ...customStyle };

    // 应用样式
    Object.entries(finalStyle).forEach(([key, value]) => {
      const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      label.style.setProperty(cssKey, value);
    });

    label.style.cssText += `
      position: absolute;
      display: none;
      opacity: 0;
      transform: translateY(-10px) scale(0.95);
      transition: opacity 0.3s ease, transform 0.3s ease;
      white-space: nowrap;
      pointer-events: auto;
      user-select: none;
      line-height: 1.2;
    `;

    label.textContent = config.text;

    // 添加悬停效果
    if (config.clickable) {
      label.style.cursor = 'pointer';
      label.addEventListener('mouseenter', () => {
        label.style.transform += ' scale(1.05)';
      });

      label.addEventListener('mouseleave', () => {
        label.style.transform = label.style.transform.replace(
          ' scale(1.05)',
          ''
        );
      });
    }

    const container = document.getElementById('v2x-label-container');
    if (container) {
      container.appendChild(label);
    }

    return label;
  }

  /**
   * 更新单个标签位置
   */
  private updateLabelPosition(instance: LabelInstance): void {
    let worldPosition: THREE.Vector3 | null = null;

    // 1. 最高优先级: 使用 updateCallback
    if (instance.config.updateCallback) {
      worldPosition = instance.config.updateCallback();
    }
    // 2. 其次: 使用 targetPosition
    else if (instance.config.targetPosition) {
      worldPosition = instance.config.targetPosition;
    }
    // 3. 最低: 使用 target object
    else if (instance.config.target) {
      worldPosition = new THREE.Vector3();
      instance.config.target.getWorldPosition(worldPosition);
    }

    // 如果无法获取位置，或者回调返回null，则隐藏标签并停止处理
    if (!worldPosition) {
      if (instance.visible) {
        instance.element.style.display = 'none';
        instance.visible = false;
      }
      return;
    }

    // 检查是否在相机视锥内和距离范围内
    const isInView = this.isPositionInView(worldPosition);
    const distance = this.camera.position.distanceTo(worldPosition);
    const maxDistance = instance.config.maxDistance ?? Infinity;

    if (instance.visible && (!isInView || distance > maxDistance)) {
      instance.element.style.display = 'none';
      instance.visible = false;
    } else if (!instance.visible && isInView && distance <= maxDistance) {
      instance.element.style.display = '';
      instance.visible = true;
    }

    if (instance.visible) {
      const screenPosition = this.worldToScreen(worldPosition);
      instance.element.style.transform = `translate(${screenPosition.x}px, ${screenPosition.y}px)`;
      instance.lastPosition.copy(worldPosition);
    }
  }

  /**
   * 检查位置是否在视野内
   */
  private isPositionInView(worldPosition: THREE.Vector3): boolean {
    const vector = worldPosition.clone();
    vector.project(this.camera);

    return (
      Math.abs(vector.x) <= 1 &&
      Math.abs(vector.y) <= 1 &&
      Math.abs(vector.z) <= 1
    );
  }

  /**
   * 世界坐标转屏幕坐标
   */
  private worldToScreen(worldPosition: THREE.Vector3): {
    x: number;
    y: number;
  } {
    const vector = worldPosition.clone();
    vector.project(this.camera);

    const containerRect = this.container.getBoundingClientRect();
    const x = (vector.x * 0.5 + 0.5) * containerRect.width;
    const y = (vector.y * -0.5 + 0.5) * containerRect.height;

    return { x, y };
  }

  /**
   * 更新所有标签位置
   */
  private updateAllLabels(): void {
    this.labels.forEach(instance => {
      if (instance.visible || instance.needsUpdate) {
        this.updateLabelPosition(instance);
      }
    });
  }

  /**
   * 批量添加标签
   */
  addLabels(configs: LabelConfig[]): string[] {
    return configs.map(config => this.addLabel(config));
  }

  /**
   * 批量移除标签
   */
  removeLabels(ids: string[]): boolean[] {
    return ids.map(id => this.removeLabel(id));
  }

  /**
   * 获取所有标签信息
   */
  getAllLabels(): Array<{
    id: string;
    config: LabelConfig;
    visible: boolean;
  }> {
    return Array.from(this.labels.entries()).map(([id, instance]) => ({
      id,
      config: instance.config,
      visible: instance.visible,
    }));
  }

  /**
   * 启用/禁用标签管理器
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;

    if (enabled) {
      this.startUpdateLoop();
    } else {
      this.stopUpdateLoop();
    }

    this.labels.forEach(instance => {
      instance.element.style.display =
        enabled && instance.visible ? 'block' : 'none';
    });
  }

  /**
   * 清理所有标签
   */
  dispose(): void {
    this.stopUpdateLoop();

    this.labels.forEach(instance => {
      instance.element.remove();
    });

    this.labels.clear();

    const container = document.getElementById('v2x-label-container');
    if (container) {
      container.remove();
    }

    // 移除事件监听器
    window.removeEventListener('resize', this.handleResize.bind(this));
    this.container.removeEventListener('click', this.handleClick.bind(this));
    this.container.removeEventListener(
      'mousemove',
      this.handleMouseMove.bind(this)
    );
  }
}

// 导出工厂函数
export function createLabelManager(
  camera: THREE.Camera,
  container: HTMLElement
): LabelManager {
  return new LabelManager(camera, container);
}
